import { Edit, PersonAdd, Phone } from "@mui/icons-material";
import { Box, Chip, Divider, IconButton, ListItem, ListItemIcon, ListItemText, Tooltip, Typography } from "@mui/material";
import { formatUSPhone } from "../../../../utils";
import { Contact } from "../../../internal-contacts";
import { useStartInternalTwilioPersonCall } from "../../../internal-twilio-call/hooks/useStartInternalTwilioPersonCall";
import { colors } from "../../../ui/colors";
import { BlockedNumberChip } from "../../blocked-numbers/BlockedNumberChip";
import { useVirtualAssistant } from "../../context/VirtualAssistantContext";
import { UiCall } from "../types";
import { formatCallHistoryLabel, getCallDirectionLabel, getCallIconConfig, getCallStatusLabel, isUnansweredCall } from "./config/call-history.config";

interface Props {
  call: UiCall;
  isLast: boolean;
  onQuickSave: (phoneNumber: string, anchorEl: HTMLElement) => void;
  onEditContact?: (contact: any) => void;
}

export const CallItem = ({ call, isLast, onQuickSave, onEditContact }: Props) => {
  const { makeCall } = useVirtualAssistant();
  const startTwilioPersonCall = useStartInternalTwilioPersonCall();
  const { icon: Icon, color } = getCallIconConfig(call);
  const isInternal = call.provider === "webrtc";

  const handleMakeCall = () => {
    if (isInternal) {
      const peerContact: Contact = {
        uid: call.peerId,
        userUid: call.peerId,
        type: "organizationCoworker",
        name: call.displayName ?? undefined,
      };
      void startTwilioPersonCall(peerContact, "CallItem");
      return;
    }

    makeCall(call.phoneNumber);
  };

  const getCallLabel = () => getCallStatusLabel(call);

  return (
    <Box>
      <ListItem
        sx={{ px: 1, py: 1.5 }}
        secondaryAction={
          <Tooltip
            title="Call"
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  zIndex: 9999999,
                },
              },
            }}
          >
            <IconButton
              edge="end"
              size="medium"
              onClick={handleMakeCall}
              sx={{
                color: "#4caf50",
                transition: "all 0.2s",
                "&:hover": {
                  bgcolor: "rgba(76, 175, 80, 0.08)",
                  transform: "scale(1.1)",
                },
              }}
            >
              <Phone fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      >
        <ListItemIcon sx={{ minWidth: 68, display: "flex", flexDirection: "column", alignItems: "center", mr: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <Tooltip
              title={getCallDirectionLabel(call)}
              placement="left"
              slotProps={{
                popper: {
                  sx: { zIndex: 9999999 },
                },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: `${color}15`,
                }}
              >
                <Icon sx={{ color, fontSize: "0.85rem" }} />
              </Box>
            </Tooltip>

            <Box
              sx={{
                bgcolor: `${color}15`,
                px: 0.5,
                borderRadius: 1,
                minWidth: 58,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Typography
                sx={{
                  color,
                  fontSize: "0.52rem",
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  textAlign: "center",
                  whiteSpace: "nowrap",
                }}
              >
                {getCallLabel().toUpperCase()}
              </Typography>
            </Box>
          </Box>
        </ListItemIcon>

        <ListItemText
          primary={
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  color: isUnansweredCall(call) ? "error.main" : "text.primary",
                }}
              >
                {call.displayName || (isInternal ? "Internal contact" : formatUSPhone(call.phoneNumber))}
              </Typography>

              <Chip
                label={isInternal ? "Internal" : "VoIP"}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  px: 0.25,
                  bgcolor: isInternal ? `${colors.blue[500]}1a` : `${colors.orange[400]}1a`,
                  color: isInternal ? colors.blue[500] : colors.orange[400],
                }}
              />

              {isInternal ? null : <BlockedNumberChip phoneNumber={call.phoneNumber} />}

              {isInternal ? null : !call.isKnown ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onQuickSave(call.phoneNumber, e.currentTarget);
                  }}
                  sx={{
                    p: 0.25,
                    color: "text.secondary",
                    "&:hover": {
                      color: "#fc9700",
                      bgcolor: "rgba(252, 151, 0, 0.08)",
                    },
                  }}
                >
                  <PersonAdd sx={{ fontSize: 13 }} />
                </IconButton>
              ) : (
                <>
                  {call.callerType && (
                    <Box sx={{ bgcolor: "action.hover", px: 0.5, borderRadius: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6rem" }}>
                        {call.callerType}
                      </Typography>
                    </Box>
                  )}
                  {call.contact && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditContact?.(call.contact);
                      }}
                      sx={{
                        p: 0.25,
                        color: "text.secondary",
                        "&:hover": {
                          color: "#fc9700",
                          bgcolor: "rgba(252, 151, 0, 0.08)",
                        },
                      }}
                    >
                      <Edit sx={{ fontSize: 13 }} />
                    </IconButton>
                  )}
                </>
              )}
            </Box>
          }
          secondary={
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem", lineHeight: 1.35 }}>
              {formatCallHistoryLabel(call)}
            </Typography>
          }
        />
      </ListItem>
      {!isLast && <Divider variant="inset" component="li" />}
    </Box>
  );
};
